import React, { useState, useEffect, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
    ContentContainer,
    SectionPage,
    Row,
    Col,
    FormHeader,
    Button,
    Heading,
    HorizontalRule,
    InPageAlert,
    Checkbox,
    ComponentLoader,
    Callout,
    ErrorSummary,
    Icon, uiIconChevronLeft
} from '@snsw-gel/react';

import { getReviewData, postApplication } from '../../../services/api-services';
import { progressStepperHelper, formHeaderLabel, formHeaderTitles } from '../../../services/progress-stepper-helper';

import styles from './Review.module.scss';
import { dataLayer } from '../../../services/gtm-dataLayer';
const ReviewPage = () => {
    const history = useHistory();
    const [error, setError] = useState(false);
    const errorSummaryRef = useRef(null);

    useEffect(() => {
        if (errorSummaryRef.current) {
            errorSummaryRef.current.focus();
        }
    }, [error]);

    const [profileConnectDetails, setProfileConnectDetails] = useState({
        poiTypes: [],
        fullName: "",
        dob: "",
        email: "",
        phone: "",
        residentialAddress: "",
        postalAddress: ""
    });

    const enteredDetails = {
        cardType: sessionStorage.getItem('CardType'),
        ...JSON.parse(sessionStorage.getItem('AdditionalDetails'))
    };

    const [isChecked, setChecked] = useState({
        termsAndConds: false,
        truthDeclaration: false,
    });

    const [showSpinner, setShowSpinner] = useState(false);

    const onChangeEvent = (inputName) => {
        setChecked({
            ...isChecked,
            [inputName]: !isChecked[inputName]
        });
    };

    useEffect(() => {
        setShowSpinner(true);
        const currentUrl = window.location.href;
        const pageName = window.location.href.split('/').pop();
        dataLayer(currentUrl, pageName);

        getReviewData().then(response => {
            setProfileConnectDetails(response);
            setShowSpinner(false);
        });
    }, []);

    const onSubmitApplication = () => async _ => {
        if (!isChecked['truthDeclaration']) {
            setError(true)
            return;
        }

        const application = {
            isSC: sessionStorage.getItem('CardType') === 'sc',
            receiveNewsletters: enteredDetails.receiveNewsletter,
            notificationMethod: enteredDetails.notificationMethod,
            mainLanguage: enteredDetails.mainLanguage,
            gender: enteredDetails.gender,
            pensionStatus: enteredDetails.pension,
            aboriginalOrTorres: enteredDetails.aboriginal
        }

        setShowSpinner(true);

        const response = await postApplication(application)
        sessionStorage.setItem('SeniorsResponse', JSON.stringify(response))

        setShowSpinner(false);

        history.push('/confirmation');
    };

    const formatNotificationMethod = value => {
        switch (value) {
            case 'Email only':
                return 'Yes, by email only';
            case 'Email and mail':
                return 'Yes, by email and mail';
            case 'none':
                return 'No';
        }
    }

    const formatAdditionalDetails = value => {
        switch (value) {
            case 'true':
                return 'Yes';
            case 'false':
                return 'No';
            case '':
                return 'Prefer not to answer';
            case 'Fully Self Funded':
                return "I don't receive a pension";

            default:
                return value;
        }
    }

    function getDocumentDescription(documentType) {
        switch (documentType) {
            case 'driverLicence':
                return 'Australian driver licence';
            case 'passport':
                return 'Passport';
            case 'medicare':
                return 'Medicare card';
            case 'birthCertificate':
                return 'Australian birth certificate';
            case 'visa':
                return 'Australian travel visa';
            case 'citizenshipCertificate':
                return 'Australian citizenship certificate';
            case 'descentCertificate':
                return 'Australian certificate of registration by descent';
            case 'pensionerConcession':
                return 'Pensioner Concession Card';
            case 'healthCare':
                return 'Health Care Card';
            case 'seniorsHealth':
                return 'Seniors Health Care Card';
            case 'immiCard':
                return 'ImmiCard';
            case 'veteranSeniorsHealth':
                return 'Veteran Seniors Health Care Card';
            case 'veteranGoldCard':
                return 'Veteran Gold Card';
            case 'veteranPensionerConcession':
                return 'Veteran Pensioner Concession Card';
            default:
                return 'Unknown document type';
        }
    }
    function formatDateString(dateString) {
        const monthMap = {
            Jan: "January",
            Feb: "February",
            Mar: "March",
            Apr: "April",
            May: "May",
            Jun: "June",
            Jul: "July",
            Aug: "August",
            Sep: "September",
            Oct: "October",
            Nov: "November",
            Dec: "December"
        };

        let parts = dateString.split('/');
        if (parts.length !== 3 || !monthMap[parts[1]]) {
            throw new Error("Invalid date format");
        }

        let day = parseInt(parts[0], 10);
        let fullMonth = monthMap[parts[1]];
        let year = parts[2];
        return `${day} ${fullMonth} ${year}`;
    }




    return (
        <>
            <FormHeader
                title={formHeaderTitles[4]}
                label={formHeaderLabel}
                stepsList={progressStepperHelper(4)}
            />
            <ContentContainer className={styles['content-container']}>
                <SectionPage>
                    {error &&
                        <div className={styles['error']}>
                            <ErrorSummary
                                ref={errorSummaryRef}
                                errors={[{ id: 'declarationbox', text: 'Tick the checkbox to submit.' }]}
                            />
                        </div>}
                    <Row>
                        <Col span={6}>
                            <InPageAlert
                                variant='info'
                                title='Check your details before submitting'>
                                <p>To avoid delays, check your details are correct before submitting.</p>
                            </InPageAlert>
                        </Col>
                    </Row>


                    <Row>
                        <Col span={12}>
                            <Heading level={2} className={styles['inf-heading']}> Your information</Heading>
                            <Heading level={4} >Details from your identity documents</Heading>
                            {profileConnectDetails.poiTypes.map((poiType, index) => (
                                <>
                                    <Heading level={5}>Document {index + 1}</Heading>
                                    <p>  {getDocumentDescription(poiType)} </p>
                                </>
                            ))}
                            <Heading level={5}> Full name</Heading>
                            <p>{profileConnectDetails.fullName}</p>
                            <Heading level={5}>Date of Birth</Heading>
                            <p>{profileConnectDetails.dob !== '' ? formatDateString(profileConnectDetails.dob) : ""}</p>
                            <Heading level={3} className={styles['contact-heading']}>Contact details from your MyServiceNSW Account</Heading>
                            <Heading level={5}>Email address</Heading>
                            <p>{profileConnectDetails.email}</p>
                            <Heading level={5}>Phone number</Heading>
                            <p>{profileConnectDetails.phone}</p>
                            <Heading level={5}>Residential address</Heading>
                            <p>{profileConnectDetails.postalAddress}</p>
                            <Row>
                                <Col span={6}>

                                    <Callout
                                        className={styles['callout']}
                                        headingElement="h3"
                                        title="Incorrect information?">
                                        <p> Go to <a target='_self' href={process.env.REACT_APP_PERSONAL_DETAILS}>‘Personal details’</a> in your MyServiceNSW Account to change it or call <a aria-label='Call us on 1 3 7 7 8 8'
                                            href='tel:137788'>13 77 88</a>.</p>
                                    </Callout>
                                </Col>
                            </Row>



                            <HorizontalRule
                                className={styles['horizontal-line']}
                            />

                            <Heading level={2}>Card information</Heading>
                            <Heading level={3} className={styles['card-heading']}>Card type</Heading>
                            <p>{enteredDetails.cardType === 'sc' ? 'NSW Seniors Card' : 'NSW Senior Savers Card'}</p>
                            <Heading level={3} className={styles['card-heading']}>Card Eligibility</Heading>
                            <Heading level={5}>How many hours of paid work are you doing per week?</Heading>
                            <p> {enteredDetails.cardType === 'sc' ? "I'm not working, or I'm working less than 20 paid hours a week" : "I'm working more than 20 paid hours a week"}</p>

                            <Button
                                className={styles['edit-button']}
                                variant='secondary'
                                id='applyButton'
                                as={Link}
                                href='/selectedCard'
                            >
                                Edit card information
                            </Button>
                            <HorizontalRule
                                className={styles['horizontal-line']}
                            />

                            <Heading level={2}>Marketing preferences</Heading>
                            <Heading level={3} className={styles['card-heading']}>Seniors Card email newsletter</Heading>
                            <Heading level={5} className={styles['marketing-heading']}>Would you like to receive the Seniors Card email newsletter?</Heading>
                            <p>{formatAdditionalDetails(enteredDetails.receiveNewsletter)}</p>
                            <Heading level={3} className={styles['card-heading']}>Notifications about exclusive offers</Heading>
                            <Heading level={5}> Would you like to be informed about exclusive Seniors Card discounts and offers?</Heading>
                            <p>{formatNotificationMethod(enteredDetails.notificationMethod)}</p>


                            {enteredDetails.notificationMethod !== 'none' &&
                                <>
                                    <Heading level={5}>What is the main language spoken at home?</Heading>
                                    <p>{enteredDetails.mainLanguage != "" ? formatAdditionalDetails(enteredDetails.mainLanguage) : "Not provided"}</p>


                                    <Heading level={5}>What is your gender?</Heading>
                                    <p> {formatAdditionalDetails(enteredDetails.gender)}</p>


                                    <Heading level={5}>What is your pension status?</Heading>
                                    <p>{formatAdditionalDetails(enteredDetails.pension)}</p>


                                    <Heading level={5}>Are you Aboriginal and/or Torres Strait Islander?</Heading>
                                    <p> {formatAdditionalDetails(enteredDetails.aboriginal)}</p>
                                </>}


                            <Button
                                className={styles['edit-button']}
                                variant='secondary'
                                id='applyButton'
                                as={Link}
                                href='/applyMarketingPreferences'
                            >
                                Edit marketing preferences
                            </Button>



                            <HorizontalRule
                                marginBottom="3.5rem"
                                marginTop="3.5rem"
                            />
                            <Row>
                                <Col span={8.6}>
                                    <Heading level={2}>Declaration</Heading>
                                    <Checkbox
                                        errorMessage="Tick the checkbox to submit."
                                        id="declarationbox"
                                        isRequired
                                        hasError={error}
                                        label='I have provided information that is truthful and complete. I understand that it is an offence to provide false and misleading information on my Seniors Card application.'
                                        name="truthDeclaration"
                                        onChange={() => onChangeEvent('truthDeclaration')}
                                        checked={isChecked['truthDeclaration']}
                                        value="truthDeclaration"
                                    />

                                    <p className={styles['marketing-heading']}>Learn more about how your personal information is handled in the <a target='_self' href="https://www.seniorscard.nsw.gov.au/privacy-policy">Privacy Collection Notice-PDF</a>.</p>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </SectionPage>

                <Row>
                    <Col span={12}>
                        <Button
                            className={styles['submit']}
                            onClick={onSubmitApplication()}
                        >
                            Submit
                        </Button>
                    </Col>
                </Row>
                <Row>
                    <Col span={12}>
                        <Button
                            iconStart={<Icon icon={uiIconChevronLeft} />}
                            className={styles['back-link']}
                            variant="link"
                            as={Link}
                            href='/applyMarketingPreferences'
                            aria-label='Go back'
                        >
                            Back
                        </Button>
                    </Col>
                </Row>
            </ContentContainer>
            {
                showSpinner && (<ComponentLoader fullPage />)
            }
        </>
    );
};

export default ReviewPage;
